import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useHttpIntegration } from "./useHttpIntegration";
import { DropDown } from "../../components/Select/DropDown";
import { DeleteIcon, ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { Editor } from "@monaco-editor/react";
import { MdEditOff, MdModeEdit, MdSave } from "react-icons/md";
import { AiTwotoneApi } from "react-icons/ai";
import { Table } from "../../components/Table/Table";
import { BrandButton } from "../../components/BrandButton/BrandButton";
import { IoMdAdd } from "react-icons/io";
import { JsonView } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

export const HttpIntegration = () => {
  const {
    selectedHttpIntegration,
    selectedConnectedSystem,
    selectedMethod,
    selectedContentType,
    requestBody,
    connectedSystems,
    methods,
    contentTypes,
    headers,
    queryParams,
    editorOptions,
    isEdited,
    isEditing,
    columns,
    columnData,
    testInputColumns,
    testInputColumnData,
    testInputs,
    testResponse,
    requestBodyMarker,
    setRequestBodyMarker,
    onEditToggle,
    setSelectedConnectedSystem,
    setSelectedMethod,
    setSelectedContentType,
    handleAddHeader,
    handleRemoveHeader,
    handleHeaderChange,
    moveHeader,
    moveQueryParam,
    handleQueryParamChange,
    handleAddQueryParam,
    handleRemoveQueryParam,
    handleEditorChange,
    onAddField,
    onTestInput,
    onValueChange,
    onSave,
  } = useHttpIntegration();

  return (
    <Box>
      <Box
        bg="rgba(255, 255, 255, 0.05)"
        backdropFilter="blur(20px)"
        borderRadius="20px"
        border="1px solid rgba(255, 255, 255, 0.1)"
        p={6}
      >
        <HStack justifyContent="space-between">
          <HStack>
            <Icon
              as={AiTwotoneApi as React.ElementType}
              color="#FF00FF"
              boxSize="24px"
            />
            <Heading fontSize="24px" color="white">
              {selectedHttpIntegration?.intgNm}
            </Heading>
          </HStack>
          <HStack>
            <Icon
              as={
                isEditing
                  ? (MdEditOff as React.ElementType)
                  : (MdModeEdit as React.ElementType)
              }
              onClick={onEditToggle}
              cursor="pointer"
              boxSize="20px"
              color="rgba(255, 255, 255, 0.8)"
              _hover={{ color: "brand.400" }}
            />
            {isEdited ? (
              <Icon
                as={MdSave as React.ElementType}
                cursor="pointer"
                boxSize="20px"
                onClick={onSave}
                color="green.400"
                _hover={{ color: "green.300" }}
              />
            ) : null}
          </HStack>
        </HStack>
      </Box>

      <Box
        bg="rgba(255, 255, 255, 0.05)"
        backdropFilter="blur(20px)"
        borderRadius="20px"
        border="1px solid rgba(255, 255, 255, 0.1)"
        p={6}
        mt={4}
      >
        <FormControl id="cs" mb={4}>
          <FormLabel color="white">Connected System</FormLabel>
          <DropDown
            selected={{
              id: selectedConnectedSystem?.uuid as string,
              name: selectedConnectedSystem?.csNm as string,
              component: <Text>{selectedConnectedSystem?.csNm as string}</Text>,
            }}
            title={<Text></Text>}
            options={connectedSystems.map((cs, index) => ({
              id: cs.uuid,
              name: cs.csNm,
              component: <Text key={index}>{cs.csNm}</Text>,
            }))}
            onOptionChange={(option) => {
              if (!Array.isArray(option)) {
                setSelectedConnectedSystem(
                  connectedSystems.find((cs) => cs.uuid === option.id)
                );
              }
            }}
            disabled={!isEditing}
          />
        </FormControl>

        <FormControl id="methods" mb={4}>
          <FormLabel color="white">Method</FormLabel>
          <DropDown
            selected={{
              id: selectedMethod as string,
              name: selectedMethod as string,
              component: <Text>{selectedMethod}</Text>,
            }}
            title={<Text></Text>}
            options={methods.map((method, index) => ({
              id: method,
              name: method,
              component: <Text key={index}>{method}</Text>,
            }))}
            onOptionChange={(option) => {
              if (!Array.isArray(option)) {
                setSelectedMethod(option.id);
              }
            }}
            disabled={!isEditing}
          />
        </FormControl>

        {selectedMethod !== "GET" && (
          <FormControl id="content-type" mb={4}>
            <FormLabel color="white">Content type</FormLabel>
            <DropDown
              selected={{
                id: selectedContentType as string,
                name: selectedContentType as string,
                component: <Text>{selectedContentType}</Text>,
              }}
              title={<Text></Text>}
              options={contentTypes.map((type, index) => ({
                id: type,
                name: type,
                component: <Text key={index}>{type}</Text>,
              }))}
              onOptionChange={(option) => {
                if (!Array.isArray(option)) {
                  setSelectedContentType(option.id);
                }
              }}
              disabled={!isEditing}
            />
          </FormControl>
        )}

        <FormControl id="name" mb={4}>
          <FormLabel color="white">Name</FormLabel>
          <Input
            placeholder="Enter integration name"
            focusBorderColor="brand.500"
            value={selectedHttpIntegration?.intgNm}
            onChange={(e) => onValueChange("intgNm", e.target.value)}
            isReadOnly={!isEditing}
          />
        </FormControl>

        <FormControl id="description" mb={4}>
          <FormLabel color="white">Description</FormLabel>
          <Textarea
            placeholder="Enter service description"
            focusBorderColor="brand.500"
            value={selectedHttpIntegration?.intgDesc}
            onChange={(e) => onValueChange("intgDesc", e.target.value)}
            isReadOnly={!isEditing}
          />
        </FormControl>

        <FormControl id="relativePath" mb={4}>
          <FormLabel color="white">Relative Path</FormLabel>
          <Input
            placeholder="Enter relative path"
            focusBorderColor="brand.500"
            value={selectedHttpIntegration?.relativePath}
            onChange={(e) => onValueChange("relativePath", e.target.value)}
            isReadOnly={!isEditing}
          />
        </FormControl>

        <FormControl id="relativePath" mb={4}>
          <FormLabel color="white">Absolute Path</FormLabel>
          <Input
            placeholder="Enter relative path"
            focusBorderColor="brand.500"
            value={`${selectedConnectedSystem?.baseUrl}${selectedHttpIntegration?.relativePath}`}
            isReadOnly={true}
          />
        </FormControl>

        <Flex gap={2}>
          <Box width="33%">
            <FormControl id="headers" mb={4}>
              <FormLabel color="white">Headers</FormLabel>
              <Stack spacing={3}>
                {headers.map((header, index) => (
                  <HStack key={header.id} spacing={3}>
                    <Input
                      placeholder="Key"
                      value={header.headNm}
                      onChange={(e) =>
                        handleHeaderChange(header.id, "headNm", e.target.value)
                      }
                      focusBorderColor="brand.500"
                      isReadOnly={!isEditing}
                    />

                    <Center
                      width="100%"
                      height="40px"
                      border="1px solid"
                      borderColor="gray.300" // Chakra UI border color
                      borderRadius="md" // Chakra UI rounded corners
                    >
                      <Editor
                        height="30px" // Fixed height for one line
                        defaultLanguage="python"
                        defaultValue="Value"
                        value={header.headVal}
                        theme="vs-dark"
                        onChange={(e) => {
                          if (e) handleHeaderChange(header.id, "headVal", e);
                        }}
                        options={{
                          lineNumbers: "off",
                          minimap: { enabled: false },
                          scrollbar: {
                            vertical: "hidden",
                            horizontal: "hidden",
                          },
                          wordWrap: "off",
                          scrollBeyondLastLine: false,
                          overviewRulerLanes: 0,
                          hideCursorInOverviewRuler: true,
                          folding: false,
                          renderLineHighlight: "none",
                          tabCompletion: "off",
                          lineDecorationsWidth: 0,
                          overviewRulerBorder: false,
                          cursorStyle: "line", // Standard cursor style
                          fontSize: 14, // Font size similar to input
                          readOnly: !isEditing,
                        }}
                      />
                    </Center>

                    <Button
                      size="sm"
                      onClick={() => handleRemoveHeader(header.id)}
                      isDisabled={!isEditing}
                    >
                      <DeleteIcon />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => moveHeader(index, "up")}
                      isDisabled={!isEditing || index === 0}
                    >
                      <ChevronUpIcon />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => moveHeader(index, "down")}
                      isDisabled={!isEditing || index === headers.length - 1}
                    >
                      <ChevronDownIcon />
                    </Button>
                  </HStack>
                ))}
                <BrandButton
                  size="md"
                  colorScheme="brand"
                  variant="solid"
                  onClick={handleAddHeader}
                  isDisabled={!isEditing}
                >
                  Add Header
                </BrandButton>
              </Stack>
            </FormControl>

            <FormControl id="queryParams" mb={4}>
              <FormLabel color="white">Query Parameters</FormLabel>
              <Stack spacing={3}>
                {queryParams.map((query, index) => (
                  <HStack key={query.id} spacing={3}>
                    <Input
                      placeholder="Key"
                      value={query.paramNm}
                      onChange={(e) =>
                        handleQueryParamChange(
                          query.id,
                          "paramNm",
                          e.target.value
                        )
                      }
                      focusBorderColor="brand.500"
                      isReadOnly={!isEditing}
                    />

                    <Center
                      width="100%"
                      height="40px"
                      border="1px solid"
                      borderColor="gray.300" // Chakra UI border color
                      borderRadius="md" // Chakra UI rounded corners
                    >
                      <Editor
                        height="30px" // Fixed height for one line
                        defaultLanguage="python"
                        defaultValue="Value"
                        value={query.paramVal}
                        theme="vs-dark"
                        onChange={(e) => {
                          if (e)
                            handleQueryParamChange(query.id, "paramVal", e);
                        }}
                        options={{
                          lineNumbers: "off",
                          minimap: { enabled: false },
                          scrollbar: {
                            vertical: "hidden",
                            horizontal: "hidden",
                          },
                          wordWrap: "off",
                          scrollBeyondLastLine: false,
                          overviewRulerLanes: 0,
                          hideCursorInOverviewRuler: true,
                          folding: false,
                          renderLineHighlight: "none",
                          tabCompletion: "off",
                          lineDecorationsWidth: 0,
                          overviewRulerBorder: false,
                          cursorStyle: "line", // Standard cursor style
                          fontSize: 14, // Font size similar to input
                          readOnly: !isEditing,
                        }}
                      />
                    </Center>

                    <Button
                      size="sm"
                      onClick={() => handleRemoveQueryParam(query.id)}
                      isDisabled={!isEditing}
                    >
                      <DeleteIcon />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => moveQueryParam(index, "up")}
                      isDisabled={!isEditing || index === 0}
                    >
                      <ChevronUpIcon />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => moveQueryParam(index, "down")}
                      isDisabled={
                        !isEditing || index === queryParams.length - 1
                      }
                    >
                      <ChevronDownIcon />
                    </Button>
                  </HStack>
                ))}
                <BrandButton
                  size="md"
                  colorScheme="brand"
                  variant="solid"
                  onClick={handleAddQueryParam}
                  isDisabled={!isEditing}
                >
                  Add Query Parameter
                </BrandButton>
              </Stack>
            </FormControl>

            {selectedMethod !== "GET" && (
              <FormControl id="body" mb={4}>
                <FormLabel color="white">Body</FormLabel>
                <Box
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={2}
                >
                  <Editor
                    value={requestBody}
                    defaultLanguage="json"
                    theme="vs-dark"
                    height="25vh"
                    width="100%"
                    options={editorOptions}
                    onChange={handleEditorChange}
                    onValidate={(marker) => setRequestBodyMarker(marker)}
                  />
                </Box>
              </FormControl>
            )}
          </Box>
          <Box width="33%">
            <HStack justifyContent="space-between" p={3}>
              <Heading fontSize="22px" color="white">
                Test inputs
              </Heading>
            </HStack>
            <Table
              data={testInputColumnData}
              columns={testInputColumns}
              paginationEnabled={false}
            />
            <HStack justifyContent="flex-end" py={3} mt={2}>
              <BrandButton onClick={onTestInput}>Test input</BrandButton>
            </HStack>
          </Box>
          <Box width="33%">
            <HStack justifyContent="space-between" p={3}>
              <Heading fontSize="22px" color="white">
                Parameters
              </Heading>
              {isEditing ? (
                <Icon
                  as={IoMdAdd as React.ElementType}
                  cursor="pointer"
                  boxSize="20px"
                  onClick={onAddField}
                  color="rgba(255, 255, 255, 0.8)"
                  _hover={{ color: "brand.400" }}
                />
              ) : (
                <></>
              )}
            </HStack>
            <Table
              data={columnData}
              columns={columns}
              paginationEnabled={false}
            />
          </Box>
        </Flex>
      </Box>

      {testResponse && (
        <Box
          mt={4}
          bg="rgba(255, 255, 255, 0.05)"
          backdropFilter="blur(20px)"
          borderRadius="20px"
          border="1px solid rgba(255, 255, 255, 0.1)"
          p={6}
        >
          <Text p={3} fontWeight="600" fontSize="18px" color="white">
            Result
          </Text>
          <Box
            bg="rgba(0, 0, 0, 0.3)"
            borderRadius="12px"
            border="1px solid rgba(255, 255, 255, 0.1)"
            p={4}
          >
            <JsonView data={testResponse} clickToExpandNode />
          </Box>
        </Box>
      )}
    </Box>
  );
};
